import './ChatInput.css'

import { ChangeEvent, FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'

import { cx } from '../utilities/cx'
import { Button } from './Button'
import { Icon } from './Icon'
import { ScrollArea, ScrollPosition } from './ScrollArea'

export type ChatInputProps = {
  className?: string
  value: string
  placeholder?: string
  disabled?: boolean
  onChange: (value: string) => void
  onSubmit: () => void
  /** Show mic control for Rheya STT */
  showMic?: boolean
  recording?: boolean
  micBusy?: boolean
  levels?: number[]
  onMicPress?: () => void
  onCancelRecording?: () => void
  onSendRecording?: () => void
}

export const ChatInput = (props: ChatInputProps) => {
  const {
    className,
    value,
    placeholder,
    disabled,
    onChange,
    onSubmit,
    showMic = false,
    recording = false,
    micBusy = false,
    levels,
    onMicPress,
    onCancelRecording,
    onSendRecording,
  } = props
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const shouldRefocusRef = useRef(false)

  const scrollPosition = useMemo<ScrollPosition>(() => ({ vertical: 'bottom' }), [value])

  useEffect(() => {
    if (disabled) {
      shouldRefocusRef.current = document.activeElement === textareaRef.current

      const handleFocusIn = (event: FocusEvent) => {
        if (event.target !== textareaRef.current) {
          shouldRefocusRef.current = false
        }
      }
      document.addEventListener('focusin', handleFocusIn)
      return () => document.removeEventListener('focusin', handleFocusIn)
    }

    if (shouldRefocusRef.current) {
      shouldRefocusRef.current = false
      textareaRef.current?.focus()
    }

    return undefined
  }, [disabled])

  useEffect(() => {
    if (recording || micBusy) return
    const textarea = textareaRef.current
    const frame = textarea?.parentElement?.parentElement?.parentElement?.parentElement
    if (!textarea || !frame) return

    const computed = getComputedStyle(textarea)
    const lineHeight = parseFloat(computed.lineHeight)
    const paddingY = parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom)
    const borderY = parseFloat(getComputedStyle(frame).borderTopWidth) * 2
    const maxLines = 5

    textarea.style.height = '0'
    frame.style.height = ''
    const contentHeight = textarea.scrollHeight

    const textHeight = contentHeight - paddingY
    const lines = Math.ceil(textHeight / lineHeight)
    const visibleLines = Math.min(lines, maxLines)
    const visibleTextHeight = visibleLines * lineHeight

    textarea.style.height = `${contentHeight}px`

    if (lines > 1) {
      frame.style.height = `${visibleTextHeight + paddingY + borderY}px`
    } else {
      textarea.style.height = `${contentHeight - borderY}px`
      frame.style.height = ''
    }
  }, [value, recording, micBusy])

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault()
    if (!value.trim() || disabled || recording || micBusy) return
    onSubmit()
    textareaRef.current?.focus()
  }

  const frameClasses = cx('chat-input__frame', {
    'chat-input__frame--focused': !disabled && isFocused && !recording,
    'chat-input__frame--disabled': disabled,
    'chat-input__frame--recording': recording || micBusy,
  })

  if (recording || micBusy) {
    const bars = levels ?? Array(24).fill(0.12)
    return (
      <div className={cx('chat-input', 'chat-input--recording', className)}>
        <button
          type="button"
          className="chat-input__side-btn"
          aria-label="Cancel recording"
          disabled={micBusy}
          onClick={onCancelRecording}
        >
          <span className="chat-input__side-icon">
            <Icon name="x" />
          </span>
        </button>
        <div className={frameClasses} aria-label="Recording">
          <div className="chat-input__waveform" aria-hidden>
            {bars.map((level, i) => (
              <span
                key={i}
                className="chat-input__wave-bar"
                style={{ height: `${Math.round(12 + level * 28)}px` }}
              />
            ))}
          </div>
          {micBusy ? <span className="chat-input__recording-label">Sending…</span> : null}
        </div>
        <button
          type="button"
          className="chat-input__side-btn chat-input__side-btn--send"
          aria-label="Send recording"
          disabled={micBusy}
          onClick={onSendRecording}
        >
          <span className="chat-input__side-icon">
            <Icon name="send" />
          </span>
        </button>
      </div>
    )
  }

  return (
    <form className={cx('chat-input', className)} onSubmit={handleSubmit}>
      {showMic ? (
        <button
          type="button"
          className="chat-input__side-btn"
          aria-label="Record voice message"
          disabled={disabled}
          onClick={onMicPress}
        >
          <span className="chat-input__side-icon">
            <Icon name="mic" />
          </span>
        </button>
      ) : null}
      <div className={frameClasses}>
        <ScrollArea vertical fitParent scroll={scrollPosition}>
          <textarea
            ref={textareaRef}
            className="chat-input__textarea"
            value={value}
            placeholder={placeholder}
            aria-label={placeholder ?? 'Message'}
            disabled={disabled}
            rows={1}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </ScrollArea>
      </div>
      <Button
        className="chat-input__button"
        type="submit"
        kind="primary"
        icon="send"
        ariaLabel="Send message"
        disabled={disabled || !value.trim()}
      />
    </form>
  )
}
